import { useState } from 'react';

import { ExportOutlined } from '@ant-design/icons';
import { AutoComplete, Empty, Select, Spin } from 'antd';
import { isSharedIdentificationFeatureEnabled } from 'constant';
import { useGetGroupOffersInfiniteInfiniteQuery } from 'services';
import useDebounce from 'hooks/useDebounce';
import { OFFER_STATUS } from 'utils';

import { mutedNoteStyle } from './muted-text-style';
import PostOrderOnlyNotice from './post-order-only-notice';
import { isPostOrder } from './share-identification';

/**
 * Gruba ait olmayan maddelerin (isExcludeGrouped=true) debounce'lu, sonsuz kaydırmalı araması.
 * Select ve AutoComplete görünümleri aynı veri ve sayfalama mantığını paylaşır.
 */
function useGroupableArticleSearch() {
  const [searchText, setSearchText] = useState('');
  const { data, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useGetGroupOffersInfiniteInfiniteQuery({ title: searchText || undefined, isExcludeGrouped: true });
  const { handleDebounce, cancelDebounce } = useDebounce();

  const pages = data?.pages ?? [];
  const rawArticles = pages.flatMap((page) => page['hydra:member']);

  const articles = Array.from(
    new Map(rawArticles.map((article) => [article.id, article])).values(),
  );

  const handleSearch = (value) => {
    handleDebounce({
      callback: () => setSearchText(value),
    });
  };

  const handlePopupScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 20 && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  };

  const resetSearch = () => {
    cancelDebounce();
    setSearchText('');
  };

  return { articles, isFetching, handleSearch, handlePopupScroll, resetSearch };
}

const toSelectedArticle = (article) => ({
  id: article.id,
  '@id': article['@id'],
  label: article.title,
  status: article.status,
  step: article.step,
});

const stopPropagation = (e) => e.stopPropagation();

/**
 * Seçeneğin satırda görünen notu ve seçilebilirliği. Pencerede zaten seçilmiş madde, Telif Sonrası şartından önce
 * gelir. Seçilemeyen maddeler gizlenmez: istemcide satır çıkarmak ilk sayfanın liste yüksekliğini doldurmamasına ve
 * sonsuz kaydırmanın durmasına yol açabilir.
 */
const getOptionState = (article, { selectedArticleIds, postOrderOnly }) => {
  if (selectedArticleIds.includes(article.id)) return { note: 'Listede', disabled: true };
  if (!postOrderOnly) return { note: null, disabled: false };

  const statusLabel = OFFER_STATUS[article.status];
  if (isPostOrder(article)) return { note: statusLabel ?? null, disabled: false };

  return {
    note: [statusLabel, "Telif Sonrası'nda değil"].filter(Boolean).join(' · '),
    disabled: true,
  };
};

/**
 * Sonuç satırı. Bağlantı maddeyi yeni sekmede açar; tıklama seçime dönüşmez, liste açık kalır.
 * Pasif satırda da çalışır.
 */
function ArticleOptionLabel({ article, note }) {
  return (
    <div className="gx-d-flex gx-align-items-center gx-justify-content-between" style={{ gap: 8 }}>
      <span className="gx-text-truncate">{article.title}</span>
      <div className="gx-d-flex gx-align-items-center" style={{ gap: 8 }}>
        {note && <span style={mutedNoteStyle}>{note}</span>}
        <a
          href={`/madde-detay/${article.id}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Yeni sekmede aç"
          style={{ cursor: 'pointer' }}
          onMouseDown={stopPropagation}
          onClick={stopPropagation}
        >
          <ExportOutlined style={{ fontSize: 14 }} />
        </a>
      </div>
    </div>
  );
}

/**
 * Arama metni input'un kendi değeri olduğu için odak kaybında ve sekme değişiminde silinmez;
 * tek seçimli Select liste kapanınca aramayı temizlediğinden AutoComplete kullanılır.
 */
function ArticleAutoComplete({ onSelect, selectedArticleIds, postOrderOnly }) {
  const [inputValue, setInputValue] = useState('');
  const { articles, isFetching, handleSearch, handlePopupScroll, resetSearch } =
    useGroupableArticleSearch();

  const handleInputSearch = (value) => {
    setInputValue(value);
    handleSearch(value);
  };

  const handleSelect = (_, option) => {
    onSelect(toSelectedArticle(option.article));
    setInputValue('');
    resetSearch();
  };

  return (
    <AutoComplete
      className="gx-mt-2 gx-w-100"
      placeholder="Madde ara ve ekle"
      value={inputValue}
      onSearch={handleInputSearch}
      onSelect={handleSelect}
      options={articles.map((article) => {
        const { note, disabled } = getOptionState(article, { selectedArticleIds, postOrderOnly });
        return {
          value: article['@id'],
          label: <ArticleOptionLabel article={article} note={note} />,
          disabled,
          article,
        };
      })}
      onPopupScroll={handlePopupScroll}
      dropdownRender={(menu) => (
        <>
          {postOrderOnly && <PostOrderOnlyNotice />}
          {menu}
        </>
      )}
      notFoundContent={
        isFetching ? <Spin size="small" /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      }
    />
  );
}

function ArticleSelect({ onSelect }) {
  const { articles, isFetching, handleSearch, handlePopupScroll, resetSearch } =
    useGroupableArticleSearch();

  const handleSelect = (_, option) => {
    onSelect(toSelectedArticle(option.article));
    resetSearch();
  };

  return (
    <Select
      className="gx-mt-2 gx-w-100"
      placeholder="Madde ara ve ekle"
      loading={isFetching}
      value={null}
      onSelect={handleSelect}
      options={articles.map((article) => ({
        label: article.title,
        value: article['@id'],
        article,
      }))}
      onSearch={handleSearch}
      onPopupScroll={handlePopupScroll}
      filterOption={false}
      showSearch
    />
  );
}

/**
 * Infinite scroll destekli madde arama kutusu.
 * Sadece herhangi bir gruba ait olmayan maddeleri listeler (isExcludeGrouped=true).
 * Bayrak kapalıyken eski Select görünümü kullanılır; pasif satır davranışı yalnız AutoComplete'tedir.
 * @param {Object} props
 * @param {Function} props.onSelect - Madde seçildiğinde callback. ({ id, '@id': string, label: string, status: number, step: number })
 * @param {number[]} [props.selectedArticleIds=[]] - Pencerede seçili (kaydedilmemiş olsa da) maddeler; listede "Listede" olarak pasif
 * @param {boolean} [props.postOrderOnly=false] - true ise yalnız Telif Sonrası'ndaki maddeler seçilebilir (kartı ortak grup)
 */
function SearchGroupableArticle({ onSelect, selectedArticleIds = [], postOrderOnly = false }) {
  return (
    <div>
      <label>
        Madde Ekle{' '}
        <span style={{ color: '#f5222d' }}>*</span>
      </label>
      {isSharedIdentificationFeatureEnabled ? (
        <ArticleAutoComplete
          onSelect={onSelect}
          selectedArticleIds={selectedArticleIds}
          postOrderOnly={postOrderOnly}
        />
      ) : (
        <ArticleSelect onSelect={onSelect} />
      )}
    </div>
  );
}

export default SearchGroupableArticle;
